/**
 * ATL-1445 stage 3, P0 — the Firestore-only writer REGISTRY: pure data, no runtime dependencies.
 *
 * Split from `firestoreOnlyWriterUsage.ts` (the reporter) so that the capability event
 * (`writeBundleCapability.ts`) and the census test can read the registry and its version without
 * pulling in the jotai store / AtomsProvider graph the reporter needs. The contract for the event
 * these writers emit is documented on the reporter.
 */
export const FIRESTORE_ONLY_WRITER_EVENT = 'firestore_only_writer_used'

/**
 * The registry's generation. Bump it in the PR that adds, renames or removes a writer, OR adds a call
 * site to an existing writer (`calls` grows) — and give every writer so changed
 * `addedInRegistryVersion` = the new value — and, in the same change, freeze the new version in the
 * census (`FROZEN_REGISTRY` in `firestoreOnlyWriterCensus.test.js`). CI fails when the highest frozen
 * version is not this constant, when the current snapshot differs from the registry, or when a
 * changed writer does not carry the version it changed in. Once a version is released, the next
 * change to any writer needs a new version.
 *
 * WHY IT EXISTS: the gate reads the ABSENCE of `firestore_only_writer_used` as "this organization
 * does not use the writer". A bundle whose registry predates a writer cannot emit it, so its silence
 * about that writer is not evidence. Every bundle therefore states its registry version — on each
 * usage event (`writer_registry_version`) and on `write_w_capability_reported`
 * (`firestore_only_writer_registry_version`) — and the gate counts a writer's absence only from
 * devices whose bundles are at or above that writer's `addedInRegistryVersion`.
 */
export const FIRESTORE_ONLY_WRITER_REGISTRY_VERSION = 1

/**
 * What the stage-3 plan (§5 "Firestore-only writers") decides for the writer. INFORMATIONAL for the
 * gate PR and reviewers — see the contract on `firestoreOnlyWriterUsage.ts` for why it is not sent.
 *
 *   route      needs a backend route (stage 2f) before a cut-over org may use it
 *   convert    a backend route exists; in Supabase mode the Firestore half is skipped
 *   disable    no W route; disabled for a cut-over org (the reverse mirror owns the data)
 *   dead-code  no caller found; to be deleted (P14). Any event is a finding: the census was wrong
 *   outside-w  stays a Firebase write after the flip (blobs, marketing lists); reported, not blocking
 */
export type FirestoreOnlyWriterDisposition = 'route' | 'convert' | 'disable' | 'dead-code' | 'outside-w'

export interface FirestoreOnlyWriterEntry {
  /** Source file, relative to `apps/atlas`. */
  readonly file: string
  /**
   * The named function that must contain the instrumentation call(s) — the writer itself, or, for a
   * write made inline in a screen, the nearest enclosing NAMED function.
   */
  readonly fn: string
  /**
   * The `FIRESTORE_ONLY_WRITER_REGISTRY_VERSION` from which this writer's instrumentation is what this
   * entry says: the version that introduced it, or the later one that gave it a new call site. A
   * bundle reporting a lower registry version cannot emit it (from every path), so the gate must not
   * read that bundle's silence as "unused".
   */
  readonly addedInRegistryVersion: number
  /** How many `reportFirestoreOnlyWriterUsage('<writer>', …)` calls `fn` must contain. Default 1. */
  readonly calls?: number
  readonly disposition: FirestoreOnlyWriterDisposition
  readonly trigger: 'user' | 'automatic'
  /** What it writes and where the decision comes from — for the reviewer, not the gate. */
  readonly note: string
}

/**
 * Checks every entry against `FirestoreOnlyWriterEntry` while keeping the literal keys as the
 * writer-name type (what `satisfies` would do — spelled as a generic so no newer syntax reaches the
 * bundler).
 */
const defineWriters = <T extends Record<string, FirestoreOnlyWriterEntry>>(writers: T): Readonly<T> =>
  Object.freeze(writers)

/**
 * Every Firestore-only writer the stage-3 plan names as disabled, routed, converted, dead, or
 * deliberately outside W. `firestoreOnlyWriterCensus.test.js` — run in CI by the
 * `write-cutover-gate-telemetry` guard of `.github/workflows/typecheck.yml` — fails when an entry
 * loses its call, a call names a writer (or sits in a function) this registry does not declare, or
 * a RELEASED registry version (its `FROZEN_REGISTRY`) is changed without a version bump.
 *
 * NOT listed, deliberately: the organization document, users, constants and billing writes the plan
 * keeps Firestore-owned by design (control plane, Phase 7) — they are not "writers that will not
 * exist after the flip", and instrumenting `lastActive` would emit on every app start.
 */
export const FIRESTORE_ONLY_WRITERS = defineWriters({
  // ── Route (2f): no backend write route exists yet ─────────────────────────────────────────────
  updateMovement: {
    file: 'global/Firebase/firebase.ts',
    fn: 'updateMovement',
    addedInRegistryVersion: 1,
    disposition: 'route',
    trigger: 'user',
    note: 'productMovements edit; live from ProductStockMovementScreen. product-movements has no write routes.',
  },
  deleteMovement: {
    file: 'global/Firebase/firebase.ts',
    fn: 'deleteMovement',
    addedInRegistryVersion: 1,
    disposition: 'route',
    trigger: 'user',
    note: 'productMovements delete; live from ProductStockMovementScreen. product-movements has no write routes.',
  },
  saveProductTag: {
    file: 'global/Firebase/firebase.ts',
    fn: 'saveProductTag',
    addedInRegistryVersion: 1,
    disposition: 'route',
    trigger: 'user',
    note:
      'Mirrors to /product-tags, which has no controller (404). No screen caller found on 2026-09-29 ' +
      '(only the global/Firebase/index.ts re-export) — an event would say otherwise.',
  },

  // ── Dead code: no callers, to be deleted in P14 ──────────────────────────────────────────────
  batchUpdateProducts: {
    file: 'global/Firebase/products.ts',
    fn: 'batchUpdateProducts',
    addedInRegistryVersion: 1,
    disposition: 'dead-code',
    trigger: 'user',
    note: 'Absolute product batch update with no mirror (stockWriteRegistry: UNVERIFIED, dead code).',
  },
  saveNewPricingRevision: {
    file: 'global/Firebase/firebase.ts',
    fn: 'saveNewPricingRevision',
    addedInRegistryVersion: 1,
    disposition: 'dead-code',
    trigger: 'user',
    note: 'pricingRevisions + product history, Firestore only; no caller.',
  },

  // ── Convert: POST /organizations/reset-data exists; skip the Firestore half in Supabase mode ──
  resetCollectionData: {
    file: 'global/Firebase/firebase.ts',
    fn: 'resetCollectionData',
    addedInRegistryVersion: 1,
    disposition: 'convert',
    trigger: 'user',
    note: 'ResetDataScreen, once per collection (writer_target = the collection).',
  },
  resetMovementData: {
    file: 'global/Firebase/firebase.ts',
    fn: 'resetMovementData',
    addedInRegistryVersion: 1,
    disposition: 'convert',
    trigger: 'user',
    note: 'ResetDataScreen: the movement collection group.',
  },
  resetProductStocks: {
    file: 'global/Firebase/firebase.ts',
    fn: 'resetProductStocks',
    addedInRegistryVersion: 1,
    disposition: 'convert',
    trigger: 'user',
    note: 'ResetDataScreen: absolute storeStock = 0 (mirror is the caller\'s single resetData call, ATL-773).',
  },
  deleteAllProducts: {
    file: 'global/Firebase/firebase.ts',
    fn: 'deleteAllProducts',
    addedInRegistryVersion: 1,
    disposition: 'convert',
    trigger: 'user',
    note: 'ResetDataScreen: deletes every product document.',
  },

  // ── Disable for a cut-over org: daily_stats is a view; the reverse mirror owns dailyStats ─────
  editDailyStatManually: {
    file: 'screens/Stats/EditDailyStatsScreen.js',
    fn: 'handleSave',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'user',
    note: 'Manual dailyStats field edit (admin screen).',
  },
  generateProductReportForDateFromMovements: {
    file: 'utils/validationFunctions.js',
    fn: 'generateProductReportForDateFromMovements',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'dailyProductStats movements report (save=true only).',
  },
  generateProductReportForDate: {
    file: 'utils/validationFunctions.js',
    fn: 'generateProductReportForDate',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'dailyProductStats product report; runs on CashboxTransactionScreen mount, ProductStats, MiscScreen.',
  },
  validateDailyStats: {
    file: 'utils/validationFunctions.js',
    fn: 'validateDailyStats',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'dailyStats repair (merge) when fixes are applied.',
  },
  validateDailyStatsId: {
    file: 'utils/validationFunctions.js',
    fn: 'validateDailyStatsId',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'dailyStats date repair (fix=true), per stat in a MiscScreen loop.',
  },
  saveProductStockSnapshot: {
    file: 'utils/validationFunctions.js',
    fn: 'saveProductStockSnapshot',
    addedInRegistryVersion: 1,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'productStockSnapshots + dailyStats start/end product amounts, once per store per day.',
  },
  checkDailyStatsValidation: {
    file: 'utils/dailyStatsValidationSimple.js',
    fn: 'checkDailyStatsValidation',
    addedInRegistryVersion: 1,
    calls: 2,
    disposition: 'disable',
    trigger: 'automatic',
    note: 'dailyStats lastUnreconciledCheck / lastValidationCheck stamps.',
  },

  // ── Outside W: stays a Firebase write after the flip ─────────────────────────────────────────
  saveNewMarketingList: {
    file: 'global/Firebase/firebase.ts',
    fn: 'saveNewMarketingList',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'marketingLists create; no Supabase table.',
  },
  updateMarketingListItem: {
    file: 'screens/ClientSegmentation/MarketingListDetailScreen.js',
    fn: 'updateCheckbox',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'marketingLists item done checkbox.',
  },
  toggleMarketingListDone: {
    file: 'screens/ClientSegmentation/MarketingListDetailScreen.js',
    fn: 'toggleListDone',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'marketingLists "complete" button.',
  },
  uploadProductImage: {
    file: 'global/Firebase/products.ts',
    fn: 'uploadProductImage',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage productImages/ (blob).',
  },
  deleteProductImage: {
    file: 'global/Firebase/products.ts',
    fn: 'deleteProductImage',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage productImages/ delete (blob).',
  },
  uploadSupplyInvoice: {
    file: 'global/Firebase/inventory.ts',
    fn: 'uploadSupplyInvoice',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage invoices/ (blob).',
  },
  deleteSupplyInvoice: {
    file: 'global/Firebase/inventory.ts',
    fn: 'deleteSupplyInvoice',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage invoices/ delete (blob).',
  },
  uploadPrintTemplateImage: {
    file: 'global/Firebase/firebase.ts',
    fn: 'uploadPrintTemplateImage',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage printTemplates/ (blob).',
  },
  deletePrintTemplateImage: {
    file: 'global/Firebase/firebase.ts',
    fn: 'deletePrintTemplateImage',
    addedInRegistryVersion: 1,
    disposition: 'outside-w',
    trigger: 'user',
    note: 'Firebase Storage printTemplates/ delete (blob).',
  },
})

export type FirestoreOnlyWriterName = keyof typeof FIRESTORE_ONLY_WRITERS
